import { axiosInstance } from '../App';

const BASE = '/api/secured/medical-indications';

// ── Types ────────────────────────────────────────────────────────────

export interface IndicationType {
  id: number;
  name: string;
  backendTypeId: number;
  redirectToStudyOrder: boolean;
}

export interface UploadedFile {
  tempFileId: string;
  fileName: string;
  size: number;
}

export interface SenderPermissions {
  email: boolean;
  whatsapp: boolean;
}

export interface SaveIndicationRequest {
  patientId: number;
  professionalId: number;
  indicationTypeId: number;
  indicationName: string;
  text: string;
  print: boolean;
  tempFileIds: string[];
  sendVia?: 'email' | 'whatsapp' | null;
  recipient?: string | null;
}

export interface SaveIndicationResponse {
  id: number;
  encodedPdf: string;
}

// ── API calls ────────────────────────────────────────────────────────

export async function getIndicationTypes(): Promise<IndicationType[]> {
  const { data } = await axiosInstance.get<IndicationType[]>(`${BASE}/types`);
  return data;
}

export async function getIndicationTemplate(
  typeId: number,
  patientId: number,
): Promise<string> {
  const { data } = await axiosInstance.get<{ text: string }>(`${BASE}/template`, {
    params: { typeId, patientId },
  });
  return data.text;
}

export async function getAntecedentsText(patientId: number): Promise<string> {
  const { data } = await axiosInstance.get<{ text: string }>(
    `${BASE}/patients/${patientId}/antecedents-text`,
  );
  return data.text;
}

export async function getSenderPermissions(): Promise<SenderPermissions> {
  const { data } = await axiosInstance.get<SenderPermissions>(`${BASE}/sender-permissions`);
  return data;
}

export async function uploadIndicationFile(file: File): Promise<UploadedFile> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await axiosInstance.post<UploadedFile>(`${BASE}/upload-file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function deleteUploadedFile(tempFileId: string): Promise<void> {
  await axiosInstance.delete(`${BASE}/uploaded-file/${tempFileId}`);
}

export async function saveIndication(
  request: SaveIndicationRequest,
): Promise<SaveIndicationResponse> {
  const { data } = await axiosInstance.post<SaveIndicationResponse>(`${BASE}/save`, request);
  return data;
}

export function getErrorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { message?: string } }; message?: string };
  return candidate?.response?.data?.message || candidate?.message || fallback;
}
